/* eslint-disable no-nested-ternary */
import { useContext, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ConfirmationModal from ".";
import {
  BACKGROUND_COLORS,
  COLORS,
} from "../../constants/design/colors";
import { AuthContext } from "../../contexts/AuthContext";
import { ConnectWalletContext } from "../../contexts/ConnectWalletContext";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { useOrder } from "../../hooks/api/order/useOrder";
import useScreenSize from "../../hooks/screenSize/useScreenSize";
import { useToast } from "../../hooks/toast";
import { useSFX } from "../../hooks/useSFX";
import {
  slRoiName,
  slTriggerPriceName,
  tpRoiName,
  tpTriggerPriceName,
} from "./TPSLForPositionModal.helpers";
import {
  buildConfirmationButtons,
  buildStats,
} from "./TPSLForPositionModal.builders";
import { renderTpslForm } from "./TPSLForPositionModal.render";
import {
  IClosePositionModal,
  Tpsl,
  TpslType,
} from "./TPSLForPositionModal.types";
import { useTPSLAdvanced } from "./useTPSLAdvanced";
import { useTPSLDerived } from "./useTPSLDerived";
import { useTPSLPrices } from "./useTPSLPrices";
import { useTPSLRegisters } from "./useTPSLRegisters";
import { useTPSLSync } from "./useTPSLSync";

/**
 * The modal responsible for creating and cancelling tpsl for position orders
 */
function TPSLForPositionModal({
  position,
  onHide,
  show,
  ...modalProps
}: IClosePositionModal) {
  const { t: apiError } = useTranslation("apiErrors");
  const [errorMessage, setErrorMessage] = useState<string>();
  const [loading, setLoading] = useState(false);
  const defaultTpslType = Tpsl.Price;
  const [tpType, setTpType] = useState<TpslType>(defaultTpslType);
  const [slType, setSlType] = useState<TpslType>(defaultTpslType);
  const [tpDropdownOpen, setTpDropdownOpen] = useState<boolean>(false);
  const [slDropdownOpen, setSlDropdownOpen] = useState<boolean>(false);
  const { accountSigningKeyState } = useContext(AuthContext);
  const { isMobileScreen } = useScreenSize();

  const { markPrice, indexPrice, lastPrice } = useTPSLPrices({
    position,
    active: show,
  });

  const { t } = useTranslation("app", {
    keyPrefix: "ConfirmationModal.TPSLForPositionModal",
  });
  const { addToast } = useToast();
  const { createTPSLOrders, tpslSupported, tpslTriggersForPosition } =
    useOrder();
  const triggers = tpslTriggersForPosition(position);
  const { mutate } = useGetAccount();
  const { playSound } = useSFX();
  const { getMarketPrecision } = useContext(MarketInstrumentContext);

  const { price_precision: pricePrecision, amount_precision: amountPrecision } =
    getMarketPrecision(position?.asset, position?.instrument_type);

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    control,
    clearErrors,
    trigger,
    reset,
  } = useForm({
    mode: "onChange",
  });

  const tpTriggerPrice = useWatch({ control, name: tpTriggerPriceName });
  const slTriggerPrice = useWatch({ control, name: slTriggerPriceName });
  const tpRoi = useWatch({ control, name: tpRoiName });
  const slRoi = useWatch({ control, name: slRoiName });

  const advanced = useTPSLAdvanced({
    position,
    triggers,
    control,
    setValue,
    tpTriggerPrice,
    slTriggerPrice,
    show,
  });

  const {
    triggerPriceFromRoi,
    actualTpTriggerPrice,
    actualSlTriggerPrice,
    asset,
    contractPriceStep,
    onHideModal,
    onConfirmTPSLForPosition,
  } = useTPSLDerived({
    position,
    tpTriggerPrice,
    slTriggerPrice,
    tpRoi,
    slRoi,
    desiredAdvanced: advanced.desired,
    reset,
    onHide,
    setTpType,
    setSlType,
    defaultTpslType,
    createTPSLOrders,
    mutate,
    playSound,
    addToast,
    t,
    apiError,
    setLoading,
    setErrorMessage,
  });

  const stats = useMemo(
    () =>
      buildStats(
        position,
        asset,
        pricePrecision,
        markPrice,
        amountPrecision,
        contractPriceStep.amount_step
      ),
    [position, asset, pricePrecision, markPrice, amountPrecision, contractPriceStep.amount_step]
  );

  const { setShowConnectModal } = useContext(ConnectWalletContext);

  const confirmationButtons = useMemo(
    () =>
      buildConfirmationButtons({
        accountSigningKeyState,
        t,
        setShowConnectModal,
        errors,
        onConfirmTPSLForPosition,
      }),
    [
      accountSigningKeyState,
      errors?.slLimitPrice?.type,
      errors?.slRoi?.type,
      errors?.slTriggerPrice?.type,
      errors?.tpLimitPrice?.type,
      errors?.tpRoi?.type,
      errors?.tpTriggerPrice?.type,
      errors?.tpslCloseSize?.type,
      onConfirmTPSLForPosition,
      setShowConnectModal,
      t,
    ]
  );

  const { triggerPriceValidateFn, lastEditedTp, lastEditedSl } = useTPSLSync({
    // Validation must measure against whatever "Trigger by" is set to, not mark.
    priceSource: advanced.priceSource,
    indexPrice,
    lastPrice,
    position,
    triggers,
    markPrice,
    trigger,
    clearErrors,
    tpTriggerPrice,
    slTriggerPrice,
    tpRoi,
    slRoi,
    tpType,
    slType,
    setValue,
    contractPriceStep,
    triggerPriceFromRoi,
  });

  const {
    takeProfitExpectedPnl,
    stopLossExpectedPnl,
    tpPriceRegister,
    tpGainRegister,
    slPriceRegister,
    slLossRegister,
    closeSizeRegister,
    tpLimitPriceRegister,
    slLimitPriceRegister,
  } = useTPSLRegisters({
    position,
    actualTpTriggerPrice,
    actualSlTriggerPrice,
    loading,
    register,
    triggerPriceValidateFn,
    contractPriceStep,
    configureAmount: advanced.configureAmount,
    useLimitPrice: advanced.useLimitPrice,
  });

  // Not memoized: the dependency list would have to name every field, register
  // and toggle in the form, so a memo would miss on essentially every render
  // while costing a 30-entry array to maintain.
  const form = renderTpslForm({
    position,
    errors,
    handleSubmit,
    onConfirmTPSLForPosition,
    tpPriceRegister,
    lastEditedTp,
    tpType,
    setTpType,
    tpGainRegister,
    tpDropdownOpen,
    setTpDropdownOpen,
    actualTpTriggerPrice,
    pricePrecision,
    takeProfitExpectedPnl,
    slPriceRegister,
    lastEditedSl,
    slType,
    setSlType,
    slLossRegister,
    slDropdownOpen,
    setSlDropdownOpen,
    actualSlTriggerPrice,
    stopLossExpectedPnl,
    advanced,
    closeSizeRegister,
    tpLimitPriceRegister,
    slLimitPriceRegister,
    asset: asset || "",
    amountPrecision,
    amountStep: contractPriceStep.amount_step,
    markPrice,
    indexPrice,
    lastPrice,
    t,
    errorMessage,
  });

  // TP/SL only exists on chains with a trigger executor (see ChainConfig
  // `supportsTpsl`). Callers gate the entry points too; this is the backstop.
  if (!position || !tpslSupported) {
    return null;
  }

  return (
    <ConfirmationModal
      title="TP/SL for Position"
      primaryColor={COLORS.positive.one}
      style={{
        backgroundColor: BACKGROUND_COLORS.seven,
        width: isMobileScreen ? "calc(100vw - 32px)" : 420,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "calc(100dvh - 32px)",
        overflowY: "auto",
        overscrollBehavior: "contain",
        WebkitOverflowScrolling: "touch",
      }}
      minWidth={isMobileScreen ? "calc(100vw - 32px)" : "420px"}
      stats={stats}
      confirmationButtons={confirmationButtons}
      confirmationWrapperStyle={{ display: "flex" }}
      isLoading={loading}
      onHide={onHideModal}
      show={show}
      {...modalProps}
      extras={form}
      statsFirst
    />
  );
}

export { Tpsl } from "./TPSLForPositionModal.types";
export type { TpslType } from "./TPSLForPositionModal.types";

export default TPSLForPositionModal;
